import { HIERARCHY_TYPES } from '../../lib/bubble/bubbleHelpers';
import type { PackLayoutNode, PackNode } from '../../lib/bubble/packNodeTypes';
import { infraBubbleStyles as s } from './bubbleDimColors';
import { strokeForBubble, type BubbleDatum } from '../../lib/bubble/bubbleHelpers';
import type { ViewMode } from '../../types/exploration';

export { infraBubbleStyles } from './bubbleDimColors';
export type { InfraBubbleStyles } from './bubbleDimColors';

export interface InfraVisualSet {
  circle: SVGCircleElement | null;
  nameChip: SVGGElement | null;
  sizeChip: SVGGElement | null;
}

export interface BubbleInfraContainer extends HTMLElement {
  __infraNodeIndex?: Map<PackLayoutNode, InfraVisualSet>;
  __applyInfraHover?: (node: PackLayoutNode | null, hovered: boolean) => void;
  __infraHoverNode?: PackLayoutNode | null;
}

export function isInfraHierarchyType(type: string | undefined | null): boolean {
  return !!type && HIERARCHY_TYPES.has(type) && type !== 'status';
}

export function getInfraVisualSet(
  container: HTMLElement,
  packNode: PackLayoutNode | null | undefined
): InfraVisualSet | null {
  if (!packNode) return null;
  return (container as BubbleInfraContainer).__infraNodeIndex?.get(packNode) ?? null;
}

export function applyInfraGroupFilter(
  set: InfraVisualSet | null,
  filter: string | null
): void {
  if (!set) return;
  for (const el of [set.circle, set.nameChip, set.sizeChip]) {
    if (!el) continue;
    if (filter) el.setAttribute('filter', filter);
    else el.removeAttribute('filter');
  }
}

/** Apply or clear hover styling on an infrastructure bubble + its chips. */
export function setInfraHoverState(
  container: HTMLElement,
  packNode: PackLayoutNode | null,
  hovered: boolean,
  view: ViewMode
): void {
  const infraContainer = container as BubbleInfraContainer;
  if (!hovered && infraContainer.__infraHoverNode !== packNode) return;

  if (hovered) {
    infraContainer.__infraHoverNode = packNode;
  } else {
    infraContainer.__infraHoverNode = null;
  }

  const set = packNode ? getInfraVisualSet(container, packNode) : null;
  if (!set) return;

  const isDimmed = set.circle?.dataset.sbDim === '1' || set.circle?.dataset.sbInstanceDim === '1';

  if (hovered) {
    if (set.circle && !set.circle.dataset.sbInstanceSelect) {
      set.circle.setAttribute('stroke', s.bubble.border.hover);
    }
    applyInfraGroupFilter(set, s.bubble.shadowFilter.hover);
    return;
  }

  if (set.circle) {
    let restingStroke: string;
    if (isDimmed) {
      restingStroke =
        set.circle.dataset.sbDimHasHlChildren === '1'
          ? s.bubble.border.dimWithHighlightedChildren
          : s.bubble.border.dim;
    } else {
      restingStroke = strokeForBubble(packNode as unknown as BubbleDatum, view);
    }
    if (!set.circle.dataset.sbInstanceSelect) {
      set.circle.setAttribute('stroke', restingStroke);
    }
  }

  // Dimmed infra keeps the soft default shadow (same as resting).
  applyInfraGroupFilter(set, s.bubble.shadowFilter.default);

  if (isDimmed && set.sizeChip) {
    const rect = set.sizeChip.querySelector('rect');
    rect?.removeAttribute('filter');
  }
}

